import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import {
  fetchNotifications,
  markNotificationRead as markReadThunk,
  markAllNotificationsRead as markAllReadThunk,
  bulkDeleteNotifications as bulkDeleteThunk,
} from "../../redux/slice/notificationSlice";
import Axios from "../../api-utility/axiosInstance";
import { useToast } from "../../helpers/contexts/ToastContext";
import { handleApiError } from "../../utility/handleApiError";
import Btn from "../../ui/button/Btn";
import CustomTabs from "../../ui/CustomTabs";
import ConfirmModal from "../../modals/ConfirmModal";
import { BellIcon } from "../../icons/btnIcons";
import { TickIcon } from "../../icons/otherIcons";
import NotificationItem from "./NotificationItem";
import { useOpenNotification } from "./notificationUtils";

const PAGE_SIZE = 10;

// Notification rows while the list loads: same frame as NotificationItem (compact), shimmering bars
const Bone = ({ w, h = 10, className = "" }) => (
  <span className={`table-skeleton-bone ${className}`} style={{ width: w, height: h }} />
);
const SKELETON_ROWS = [
  ["60%", "85%"],
  ["45%", "70%"],
  ["70%", "90%"],
  ["50%", "65%"],
  ["65%", "80%"],
];
const NotificationSkeleton = ({ rows = SKELETON_ROWS }) => (
  <div aria-busy="true" aria-label="Loading notifications">
    {rows.map(([title, message], i) => (
      <div key={i} className="d-flex align-items-start gap-6 px-10 py-6 border-bottom">
        <Bone w={36} h={36} className="rounded-circle flex-shrink-0" />
        <div className="flex-grow-1 min-w-0 flex-column gap-3">
          <div className="flex-between gap-4">
            <Bone w={title} h={12} />
            <Bone w={44} h={9} className="flex-shrink-0" />
          </div>
          <Bone w={message} h={9} />
        </div>
      </div>
    ))}
  </div>
);

// Keep order, drop duplicates (a refresh can bring back rows we already have)
const mergeById = (first, second) => {
  const seen = new Set();
  return [...first, ...second].filter((n) => (seen.has(n.id) ? false : seen.add(n.id)));
};

// Bell drawer: first 10, more on scroll; All / Unread, open one, mark read, clear
const NotificationPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [view, setView] = useState("all");
  const [confirmClear, setConfirmClear] = useState(false);

  // lastFetchedAt changes on every 30 s refresh in the layout
  const { totalUnread, lastFetchedAt } = useSelector((state) => state.notifications);
  const unreadCount = totalUnread || 0;

  const [list, setList] = useState({ items: [], page: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const requestId = useRef(0);
  const hasMore = list.items.length < list.total;

  const fetchPage = useCallback(
    (page) =>
      Axios.get("/tenant/notifications", {
        params: {
          page,
          limit: PAGE_SIZE,
          ...(view === "unread" ? { only_unread: "true" } : {}),
        },
      }).then((res) => {
        const data = res.data?.data || {};
        return { rows: Array.isArray(data.data) ? data.data : [], total: Number(data.total_records) || 0 };
      }),
    [view]
  );

  // First 10 (on open / tab change / after an action)
  const loadFirst = useCallback(async () => {
    const id = ++requestId.current;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { rows, total } = await fetchPage(1);
      if (id === requestId.current) setList({ items: rows, page: 1, total });
    } catch (err) {
      if (id === requestId.current) handleApiError(err, { showToast });
    } finally {
      if (id === requestId.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [fetchPage, showToast]);

  // Next 10 when scrolled near the bottom
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    const id = requestId.current;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { rows, total } = await fetchPage(list.page + 1);
      if (id === requestId.current) {
        setList((prev) => ({ items: mergeById(prev.items, rows), page: prev.page + 1, total }));
      }
    } catch (err) {
      handleApiError(err, { showToast });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetchPage, hasMore, list.page, showToast]);

  useEffect(() => {
    if (isOpen) loadFirst();
  }, [isOpen, loadFirst]);

  // 30 s refresh while open: newest on top, keep what is already scrolled in
  useEffect(() => {
    if (!isOpen || !lastFetchedAt) return;
    fetchPage(1)
      .then(({ rows, total }) =>
        setList((prev) => ({ ...prev, items: mergeById(rows, prev.items), total }))
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFetchedAt]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) loadMore();
  };

  // After an action: this list + the bell count
  const refresh = () => {
    loadFirst();
    dispatch(fetchNotifications({ page: 1, limit: PAGE_SIZE, force: true }));
  };
  const openNotification = useOpenNotification({ onRead: refresh, onLeave: onClose });

  const handleMarkRead = async (item) => {
    try {
      await dispatch(markReadThunk(item.id)).unwrap();
      refresh();
    } catch (err) {
      handleApiError(err, { showToast });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await dispatch(markAllReadThunk()).unwrap();
      refresh();
    } catch (err) {
      handleApiError(err, { showToast });
    }
  };

  // "Clear all" means every notification, not only the ones scrolled in
  const handleConfirmClear = async () => {
    try {
      const res = await Axios.get("/tenant/notifications");
      const all = res.data?.data?.data || [];
      if (all.length) await dispatch(bulkDeleteThunk(all.map((n) => n.id))).unwrap();
      showToast("Notifications cleared", "success");
      setConfirmClear(false);
      refresh();
    } catch (err) {
      handleApiError(err, { showToast });
    }
  };

  return (
    <Offcanvas show={isOpen} onHide={onClose} placement="end" className="notification-panel">
      <Offcanvas.Header closeButton className="border-bottom px-10 py-8 align-items-start">
        <div className="flex-column gap-1">
          <Offcanvas.Title as="h6" className="m-0 fw-medium primary-text d-flex align-items-center gap-3">
            Notifications
            {unreadCount > 0 && (
              <span className="badge rounded-pill bg-danger fw-normal">{unreadCount}</span>
            )}
          </Offcanvas.Title>
          <span className="fs-xs text-secondary">
            {unreadCount > 0
              ? `${unreadCount} unread update${unreadCount > 1 ? "s" : ""}`
              : "You're all caught up"}
          </span>
        </div>
      </Offcanvas.Header>

      {/* All / Unread + mark all read */}
      <div className="d-flex align-items-center justify-content-between gap-4 px-10 py-5 border-bottom">
        <CustomTabs
          variant="pills"
          activeKey={view}
          onChange={setView}
          items={[
            { key: "all", title: "All" },
            {
              key: "unread",
              title: (
                <span className="d-inline-flex align-items-center gap-2">
                  Unread
                  {unreadCount > 0 && (
                    <span className="badge rounded-pill bg-danger-subtle text-danger fw-normal">
                      {unreadCount}
                    </span>
                  )}
                </span>
              ),
            },
          ]}
          linkClassName="py-3 px-8 fs-sm"
        />
        {unreadCount > 0 && (
          <Btn
            variant="link"
            className="p-0 fs-xs text-decoration-none"
            onClick={handleMarkAllRead}
            startIcon={<TickIcon size={13} />}
            title="Mark all as read"
          />
        )}
      </div>

      <Offcanvas.Body className="p-0 custom-scroll" onScroll={onScroll}>
        {list.items.length === 0 && loading ? (
          <NotificationSkeleton />
        ) : list.items.length === 0 ? (
          <div className="flex-center flex-column text-center gap-3 px-12 h-100">
            <i
              className="flex-center ratio-1 rounded-circle bg-primary-subtle text-primary"
              style={{ width: 64 }}
            >
              <BellIcon size={28} />
            </i>
            <p className="m-0 fs-sm text-primary fw-medium">
              {view === "unread" ? "No unread notifications" : "No notifications yet"}
            </p>
            <span className="fs-xs text-secondary">
              Updates on your leads, quotations and follow-ups will show up here.
            </span>
          </div>
        ) : (
          <>
            <div className="list-group list-group-flush">
              {list.items.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  compact
                  onOpen={openNotification}
                  onMarkRead={handleMarkRead}
                />
              ))}
            </div>
            {/* Bottom of the list: loading more (two skeleton rows) / Load more / the end */}
            {loading ? (
              <NotificationSkeleton rows={SKELETON_ROWS.slice(0, 2)} />
            ) : (
              <div className="text-center py-6 fs-xs text-secondary">
                {hasMore ? (
                  <Btn
                    variant="link"
                    className="p-0 fs-xs text-decoration-none"
                    title="Load more"
                    onClick={loadMore}
                  />
                ) : (
                  "That's everything"
                )}
              </div>
            )}
          </>
        )}
      </Offcanvas.Body>

      <div className="offcanvas-footer d-flex align-items-center justify-content-between gap-4">
        {list.items.length > 0 ? (
          <Btn
            variant="link"
            className="p-0 fs-xs text-danger text-decoration-none"
            onClick={() => setConfirmClear(true)}
            title="Clear all"
          />
        ) : (
          <span />
        )}
        <Btn
          variant="outline-primary"
          className="py-3 px-8 fs-sm"
          as={Link}
          to="/notifications"
          onClick={onClose}
          title="View all notifications"
        />
      </div>

      {confirmClear && (
        <ConfirmModal
          title="Clear Notifications"
          size="sm"
          isOpen={confirmClear}
          onClose={() => setConfirmClear(false)}
          onConfirm={handleConfirmClear}
          message="Are you sure you want to clear all notifications?"
          confirmText="Yes, Clear All"
        />
      )}
    </Offcanvas>
  );
};

export default NotificationPanel;
